import { DynamicBottomSheet } from "@base/ui/DynamicBottomSheet";
import { PROFILE_COLOR } from "@features/signup/config/profileColors";
import { useUserProfileSheet } from "@features/user-profile/model/useUserProfileSheet";
import { UserProfileBottomSheet } from "@features/user-profile/ui/UserProfileBottomSheet";
import { useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useReactChatMessageMutation } from "../api/chatMessageReactionMutation";
import { chatInfiniteMessagesQueryKey, useInfiniteChatMessagesQuery } from "../api/chatMessagesQuery";
import { chatRoomHeaderQueryKey, useChatRoomHeaderQuery } from "../api/chatRoomHeaderQuery";
import { useSendChatMessageMutation } from "../api/sendChatMessageMutation";
import { formatTimeLabel } from "../lib/formatChatTime";
import { scrollToChatMessage } from "../lib/scrollToChatMessage";
import { getChatMessageReactionState } from "../model/chatMessageReaction";
import { type ChatSelectedOption, resolveChatSelectedOption } from "../model/chatVoteOption";
import type { ChatMessageReactionType, ChatMessageResponse, ChatReplyTarget } from "../model/types";
import { useChatWebSocket } from "../model/useChatWebSocket";
import { useMarkLatestChatAsRead } from "../model/useMarkLatestChatAsRead";
import { ChatMessageContextMenu } from "./ChatMessageContextMenu";
import { ChatMessageReactionBar } from "./ChatMessageReactionBar";
import { ChatMessageReplySnippet } from "./ChatMessageReplySnippet";
import { ChatReplyPreview } from "./ChatReplyPreview";
import { ChatSelectedOptionBadge } from "./ChatSelectedOptionBadge";

const SCROLL_BUTTON_THRESHOLD_PX = 100;
const LOAD_MORE_THRESHOLD_PX = 50;
const UNKNOWN_USER_NICKNAME = "알 수 없음";
const UNKNOWN_USER_DISPLAY_NAME = "(알 수 없음)";
const DEFAULT_PROFILE_IMAGE = "/assets/icons/default-profile.svg";

const THEME = {
  light: {
    sheet: "",
    title: "text-grey-black",
    headerBorder: "border-grey-divider",
    emptyTitle: "text-grey-black",
    emptyText: "text-grey-light",
    senderNickname: "text-grey-dark",
    otherBubble: "bg-grey-chat text-grey-black",
    myBubble: "bg-white border border-grey-stroke text-grey-black",
    time: "text-grey-light",
    inputContainer: "bg-white border-t border-grey-stroke",
    input: "bg-grey-chat text-grey-black placeholder:text-grey-disabled",
    scrollButton: "bg-white shadow-md text-grey-dark",
    placeholder: "메시지를 입력하세요.",
  },
  dark: {
    sheet: "!bg-[#1B1D20]",
    title: "text-white",
    headerBorder: "border-grey-dark",
    emptyTitle: "text-white",
    emptyText: "text-grey-light",
    senderNickname: "text-white",
    otherBubble: "bg-[#36363A] text-white",
    myBubble: "bg-transparent text-white border border-[#434346]",
    time: "text-grey-light",
    inputContainer: "bg-[#1C1C1E] border-t border-[#3C3C3E]",
    input: "bg-[#2A2C2F] text-white placeholder:text-grey-light",
    scrollButton: "bg-[#3A3A3C] text-white",
    placeholder: "채팅으로 의견을 남겨보세요.",
  },
} as const;

type Theme = (typeof THEME)[keyof typeof THEME];

interface ChatBottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  voteId: number;
  isDark?: boolean;
  /** 사용자가 메시지를 입력하기 시작하거나 전송할 때. 계측용이라 여러 번 호출될 수 있다. */
  onCompose?: () => void;
}

export function ChatBottomSheet({ isOpen, onClose, voteId, isDark = false, onCompose }: ChatBottomSheetProps) {
  const t = isDark ? THEME.dark : THEME.light;
  return (
    <DynamicBottomSheet isOpen={isOpen} onClose={onClose} defaultHeight={70} maxHeight={90} className={t.sheet}>
      {isOpen && <ChatContent voteId={voteId} t={t} isDark={isDark} onClose={onClose} onCompose={onCompose} />}
    </DynamicBottomSheet>
  );
}

// ---

interface ChatContentProps {
  voteId: number;
  t: Theme;
  isDark: boolean;
  onClose: () => void;
  onCompose?: () => void;
}

function ChatContent({ voteId, t, isDark, onClose, onCompose }: ChatContentProps) {
  const queryClient = useQueryClient();
  // "몰입형만 다크" 규칙에 따라 다크 채팅 = 몰입형 화면으로 간주 (랜딩 분기 기준)
  // 투표 카드 탭으로 랜딩 시 이 채팅 바텀시트(onClose)도 함께 닫는다.
  const profileSheet = useUserProfileSheet({
    originSurface: isDark ? "immersive" : "general",
    onLanding: onClose,
  });
  const { data: header } = useChatRoomHeaderQuery(voteId);
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteChatMessagesQuery(voteId);

  // 채팅을 열 때마다 최신 데이터를 보장한다.
  // VoteDetail: commentCount 갱신 / messages: senderVoteOption 변경 반영
  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: ["votes", String(voteId)] });
    queryClient.invalidateQueries({ queryKey: chatRoomHeaderQueryKey(voteId) });
    queryClient.invalidateQueries({ queryKey: chatInfiniteMessagesQueryKey(voteId) });
  }, [voteId, queryClient]);
  const sendMessageMutation = useSendChatMessageMutation(voteId);
  const reactMessageMutation = useReactChatMessageMutation(voteId);
  useChatWebSocket(voteId);

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const isAtBottomRef = useRef(true);
  const prevCountRef = useRef(0);
  const prevScrollHeightRef = useRef(0);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [replyTarget, setReplyTarget] = useState<ChatReplyTarget | null>(null);
  const [inputFocusSignal, setInputFocusSignal] = useState(0);

  // pages[0] = 최신 페이지, pages[n] = 오래된 페이지 / 각 페이지 내 messages는 getChatMessages에서 이미 오름차순 정렬됨
  const allMessages =
    data?.pages
      .slice()
      .reverse()
      .flatMap((p) => p.messages) ?? [];
  const messagesCount = allMessages.length;
  const latestMessageId = allMessages[allMessages.length - 1]?.messageId ?? null;

  const markAsRead = useMarkLatestChatAsRead(voteId);
  useEffect(() => {
    if (latestMessageId == null) return;
    markAsRead(latestMessageId);
  }, [latestMessageId, markAsRead]);

  // 새 메시지 수신 시 하단 자동 스크롤 (하단에 있을 때만)
  useEffect(() => {
    const isInitialLoad = prevCountRef.current === 0 && messagesCount > 0;
    const isNewMessage = messagesCount > prevCountRef.current;
    prevCountRef.current = messagesCount;

    if (isInitialLoad) {
      bottomRef.current?.scrollIntoView();
    } else if (isNewMessage && isAtBottomRef.current) {
      bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messagesCount]);

  // 이전 메시지 로드 후 스크롤 위치 보정 — paint 전 동기 실행으로 점프 방지
  useLayoutEffect(() => {
    const el = scrollContainerRef.current;
    if (!el || prevScrollHeightRef.current === 0) return;
    el.scrollTop += el.scrollHeight - prevScrollHeightRef.current;
    prevScrollHeightRef.current = 0;
  }, []);

  const handleScroll = () => {
    const el = scrollContainerRef.current;
    if (!el) return;

    if (el.scrollTop <= LOAD_MORE_THRESHOLD_PX && hasNextPage && !isFetchingNextPage) {
      prevScrollHeightRef.current = el.scrollHeight;
      fetchNextPage();
    }

    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    const isAtBottom = distanceFromBottom <= SCROLL_BUTTON_THRESHOLD_PX;
    isAtBottomRef.current = isAtBottom;
    setShowScrollButton(!isAtBottom);
  };

  const scrollToBottom = () => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    setShowScrollButton(false);
    isAtBottomRef.current = true;
  };

  const handleReaction = (message: ChatMessageResponse, reaction: ChatMessageReactionType) => {
    reactMessageMutation.mutate({ message, reaction });
  };

  const handleReply = (nextReplyTarget: ChatReplyTarget) => {
    setReplyTarget(nextReplyTarget);
    setInputFocusSignal((current) => current + 1);
  };

  const handleSubmitMessage = (message: string) => {
    sendMessageMutation.mutate({ content: message, replyTo: replyTarget });
    setReplyTarget(null);
  };

  const isLoaded = !!data;
  const isEnded = header?.status === "ENDED";
  const optionA = header?.optionA ?? "";
  const optionB = header?.optionB ?? "";
  const selectedOption = resolveChatSelectedOption(header);

  return (
    <div className="flex flex-col h-full">
      <div className={`shrink-0 text-title-m text-center pb-5 border-b ${t.headerBorder} ${t.title}`}>실시간 채팅</div>

      <div className="relative flex-1 min-h-0">
        <div ref={scrollContainerRef} onScroll={handleScroll} className="h-full overflow-y-auto overscroll-contain">
          <div className={clsx("flex min-h-full flex-col", messagesCount > 0 ? "justify-end" : "justify-center")}>
            {renderMessageArea(
              isLoaded,
              allMessages,
              optionA,
              optionB,
              t,
              isFetchingNextPage,
              profileSheet.openProfile,
              handleReaction,
              handleReply,
            )}
            <div ref={bottomRef} />
          </div>
        </div>

        {showScrollButton && (
          <button
            type="button"
            className={`absolute right-5 flex h-12 w-12 items-center justify-center rounded-full ${t.scrollButton}`}
            style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 20px)" }}
            onClick={scrollToBottom}
            aria-label="최신 메시지로 이동"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path
                d="M4 8L12 16L20 8"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        )}
      </div>

      <div className={`shrink-0 ${t.inputContainer}`}>
        {isEnded ? (
          <>
            <ChatSelectedOptionBadge selectedOption={selectedOption} className="px-5 pt-3" />
            <p className="py-4 text-center text-label-m text-grey-dark">투표가 종료되어 채팅이 마감되었어요.</p>
          </>
        ) : (
          <MessageInput
            t={t}
            disabled={sendMessageMutation.isPending}
            selectedOption={selectedOption}
            replyTarget={replyTarget}
            focusSignal={inputFocusSignal}
            isDark={isDark}
            onCancelReply={() => setReplyTarget(null)}
            onReplyTargetClick={scrollToChatMessage}
            onCompose={onCompose}
            onSubmit={handleSubmitMessage}
          />
        )}
      </div>

      <UserProfileBottomSheet
        isOpen={profileSheet.isOpen}
        onClose={profileSheet.close}
        profile={profileSheet.profile}
        isDark={isDark}
        onVoteClick={profileSheet.handleVoteClick}
      />
    </div>
  );
}

function renderMessageArea(
  isLoaded: boolean,
  messages: ChatMessageResponse[],
  optionA: string,
  optionB: string,
  t: Theme,
  isFetchingMore: boolean,
  onProfileClick: (userId: number | undefined) => void,
  onReaction: (message: ChatMessageResponse, reaction: ChatMessageReactionType) => void,
  onReply: (replyTarget: ChatReplyTarget) => void,
) {
  if (!isLoaded) {
    return (
      <div className="flex h-40 items-center justify-center">
        <p className="text-label-m text-grey-light">불러오는 중...</p>
      </div>
    );
  }
  if (messages.length === 0) {
    return <EmptyState t={t} />;
  }
  return (
    <MessageList
      messages={messages}
      optionA={optionA}
      optionB={optionB}
      t={t}
      isFetchingMore={isFetchingMore}
      onProfileClick={onProfileClick}
      onReaction={onReaction}
      onReply={onReply}
    />
  );
}

// ---

interface EmptyStateProps {
  t: Theme;
}

function EmptyState({ t }: EmptyStateProps) {
  return (
    <div className="flex min-h-[200px] flex-col items-center justify-center px-5 text-center">
      <h2 className={`text-h-s ${t.emptyTitle}`}>첫 번째 메시지를 남겨보세요!</h2>
      <p className={`mt-4 whitespace-pre-line text-title-s ${t.emptyText}`}>
        {"가볍게 한마디 남기면\n다른 의견도 자연스럽게 볼 수 있어요"}
      </p>
    </div>
  );
}

// ---

interface MessageListProps {
  messages: ChatMessageResponse[];
  optionA: string;
  optionB: string;
  t: Theme;
  isFetchingMore: boolean;
  onProfileClick: (userId: number | undefined) => void;
  onReaction: (message: ChatMessageResponse, reaction: ChatMessageReactionType) => void;
  onReply: (replyTarget: ChatReplyTarget) => void;
}

interface ContextMenuTarget {
  message: ChatMessageResponse;
  anchorRect: DOMRect;
}

const LONG_PRESS_MS = 500;
const MESSAGE_TOUCH_CLASS = "select-none [-webkit-touch-callout:none] [-webkit-user-select:none]";

const blurActiveTextInput = () => {
  const activeElement = document.activeElement;
  if (
    activeElement instanceof HTMLInputElement ||
    activeElement instanceof HTMLTextAreaElement ||
    (activeElement instanceof HTMLElement && activeElement.isContentEditable)
  ) {
    activeElement.blur();
  }
};

function MessageList({
  messages,
  optionA,
  optionB,
  t,
  isFetchingMore,
  onProfileClick,
  onReaction,
  onReply,
}: MessageListProps) {
  const longPressTimerRef = useRef<number | null>(null);
  const [contextMenuTarget, setContextMenuTarget] = useState<ContextMenuTarget | null>(null);

  const clearLongPressTimer = () => {
    if (longPressTimerRef.current == null) return;
    window.clearTimeout(longPressTimerRef.current);
    longPressTimerRef.current = null;
  };

  const startLongPress = (message: ChatMessageResponse, element: HTMLElement) => {
    if (message.isMine) return;

    clearLongPressTimer();
    longPressTimerRef.current = window.setTimeout(() => {
      blurActiveTextInput();
      setContextMenuTarget({ message, anchorRect: element.getBoundingClientRect() });
      longPressTimerRef.current = null;
    }, LONG_PRESS_MS);
  };

  const closeContextMenu = () => setContextMenuTarget(null);

  return (
    <>
      <section className="px-5 py-4 space-y-4">
        {isFetchingMore && (
          <div className="flex justify-center py-2">
            <p className={`text-label-s ${t.time}`}>이전 메시지 불러오는 중...</p>
          </div>
        )}
        {messages.map((message) => (
          <MessageItem
            key={message.messageId}
            message={message}
            optionA={optionA}
            optionB={optionB}
            t={t}
            reactionState={getChatMessageReactionState(message)}
            onProfileClick={onProfileClick}
            onStartLongPress={startLongPress}
            onCancelLongPress={clearLongPressTimer}
          />
        ))}
      </section>

      {contextMenuTarget && (
        <ChatMessageContextMenu
          anchorRect={contextMenuTarget.anchorRect}
          isDark={t === THEME.dark}
          onClose={closeContextMenu}
          onReact={(reaction) => onReaction(contextMenuTarget.message, reaction)}
          onReply={() =>
            onReply({
              messageId: contextMenuTarget.message.messageId,
              senderNickname: contextMenuTarget.message.senderNickname,
              content: contextMenuTarget.message.content,
            })
          }
        />
      )}
    </>
  );
}

// ---

interface MessageItemProps {
  message: ChatMessageResponse;
  optionA: string;
  optionB: string;
  t: Theme;
  reactionState: ReturnType<typeof getChatMessageReactionState>;
  onProfileClick: (userId: number | undefined) => void;
  onStartLongPress: (message: ChatMessageResponse, element: HTMLElement) => void;
  onCancelLongPress: () => void;
}

function MessageItem({
  message,
  optionA,
  optionB,
  t,
  reactionState,
  onProfileClick,
  onStartLongPress,
  onCancelLongPress,
}: MessageItemProps) {
  const isOptionA = message.senderVoteOption === "A";
  const optionLabel = isOptionA ? optionA : optionB;
  const optionTextColor = isOptionA ? "text-secondary" : "text-primary";
  const isUnknownUser = message.senderNickname === UNKNOWN_USER_NICKNAME || !message.senderProfileIcon;
  const displayName =
    message.senderNickname === UNKNOWN_USER_NICKNAME ? UNKNOWN_USER_DISPLAY_NAME : message.senderNickname;
  const profileImage = isUnknownUser
    ? DEFAULT_PROFILE_IMAGE
    : PROFILE_COLOR[message.senderProfileIcon as keyof typeof PROFILE_COLOR];
  // 프로필 진입 가능: 타인 메시지이면서 식별 가능한 유저
  const canOpenProfile = !isUnknownUser;
  const handleProfileClick = () => onProfileClick(message.senderId);

  if (message.isMine) {
    return (
      <div className="flex justify-end px-1 py-1" data-chat-message-id={message.messageId}>
        <div className="max-w-[75%]">
          <div className="flex justify-end gap-1 mb-2">
            <span className={clsx("text-label-m", t.senderNickname)}>{displayName}</span>
            {message.senderVoteOption && (
              <span className={clsx("text-label-l max-w-[116px] truncate", optionTextColor)}>{optionLabel}</span>
            )}
          </div>
          <div className="flex items-end gap-2 justify-end">
            <span className={`text-label-s ${t.time}`}>{formatTimeLabel(message.sentAt)}</span>
            <div
              className={clsx(
                `overflow-hidden rounded-tl-2xl rounded-bl-2xl rounded-br-2xl rounded-tr-md ${t.myBubble}`,
                MESSAGE_TOUCH_CLASS,
              )}
            >
              <ChatMessageReplySnippet
                replyTo={message.replyTo}
                isDark={t === THEME.dark}
                onClick={scrollToChatMessage}
              />
              <p className="px-[14px] py-3 text-body-s">{message.content}</p>
            </div>
          </div>
          <ChatMessageReactionBar reactionState={reactionState} align="right" isDark={t === THEME.dark} />
        </div>
      </div>
    );
  }

  return (
    <div className="flex gap-2 px-1 py-1" data-chat-message-id={message.messageId}>
      <button
        type="button"
        className="flex h-10 w-10 shrink-0"
        onClick={handleProfileClick}
        disabled={!canOpenProfile}
        aria-label={`${displayName} 프로필 보기`}
      >
        <img src={profileImage} alt="" className="object-cover w-full h-full rounded-full bg-gray-200" />
      </button>
      <div className="max-w-[75%]">
        <div className="flex gap-1 mb-2">
          <button
            type="button"
            className={clsx("text-label-m", t.senderNickname)}
            onClick={handleProfileClick}
            disabled={!canOpenProfile}
          >
            {displayName}
          </button>
          {message.senderVoteOption && (
            <span className={clsx("text-label-l max-w-[116px] truncate", optionTextColor)}>{optionLabel}</span>
          )}
        </div>
        <div className="flex items-end gap-2">
          <div
            className={clsx(
              `overflow-hidden rounded-tl-md rounded-bl-2xl rounded-br-2xl rounded-tr-2xl ${t.otherBubble}`,
              MESSAGE_TOUCH_CLASS,
            )}
            onPointerDown={(event) => onStartLongPress(message, event.currentTarget)}
            onPointerUp={onCancelLongPress}
            onPointerCancel={onCancelLongPress}
            onPointerLeave={onCancelLongPress}
            onContextMenu={(event) => event.preventDefault()}
          >
            <ChatMessageReplySnippet
              replyTo={message.replyTo}
              isDark={t === THEME.dark}
              onClick={scrollToChatMessage}
            />
            <p className="px-[14px] py-3 text-body-s">{message.content}</p>
          </div>
          <span className={`text-label-s ${t.time}`}>{formatTimeLabel(message.sentAt)}</span>
        </div>
        <ChatMessageReactionBar reactionState={reactionState} align="left" isDark={t === THEME.dark} />
      </div>
    </div>
  );
}

// ---

interface MessageInputProps {
  t: Theme;
  disabled: boolean;
  selectedOption: ChatSelectedOption | null;
  replyTarget: ChatReplyTarget | null;
  focusSignal: number;
  isDark: boolean;
  onCancelReply: () => void;
  onReplyTargetClick: (messageId: number) => void;
  onCompose?: () => void;
  onSubmit: (message: string) => void;
}

function MessageInput({
  t,
  disabled,
  selectedOption,
  replyTarget,
  focusSignal,
  isDark,
  onCancelReply,
  onReplyTargetClick,
  onCompose,
  onSubmit,
}: MessageInputProps) {
  const [message, setMessage] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: message triggers DOM height recalculation
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [message]);

  useLayoutEffect(() => {
    if (focusSignal === 0) return;
    textareaRef.current?.focus();
  }, [focusSignal]);

  const handleSubmit = () => {
    const trimmed = message.trim();
    if (!trimmed || disabled) return;
    onCompose?.();
    onSubmit(trimmed);
    setMessage("");
  };

  const handleChange = (next: string) => {
    if (message.length === 0 && next.length > 0) onCompose?.();
    setMessage(next);
  };

  return (
    <div className="px-5 pt-2" style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 8px)" }}>
      <ChatReplyPreview
        replyTarget={replyTarget}
        isDark={isDark}
        onCancel={onCancelReply}
        onClick={onReplyTargetClick}
      />
      <ChatSelectedOptionBadge selectedOption={selectedOption} className="mb-2" />

      <div className="flex items-end gap-2">
        <textarea
          ref={textareaRef}
          rows={1}
          value={message}
          disabled={disabled}
          placeholder={t.placeholder}
          onChange={(e) => handleChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
              e.preventDefault();
              handleSubmit();
            }
          }}
          className={`flex-1 min-w-0 px-4 py-2 rounded-2xl outline-none text-body-s resize-none overflow-y-auto max-h-28 disabled:text-grey-disabled ${t.input}`}
        />
        <button
          type="button"
          disabled={disabled || message.trim().length === 0}
          onClick={handleSubmit}
          className="flex items-center justify-center text-white rounded-full h-10 w-10 shrink-0 bg-primary disabled:bg-grey-disabled transition-colors"
          aria-label="메시지 전송"
        >
          <img src="/assets/icons/send.svg" alt="" />
        </button>
      </div>
    </div>
  );
}
